<!-- eslint-disable -->
<template>
  <v-container class="admin-container">
    <!-- Access Denied -->
    <v-alert v-if="!isAdmin && !loading" type="error" class="my-4">
      {{ $t('admin.accessDenied') }}
    </v-alert>

    <!-- Loading -->
    <div v-else-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>

    <!-- Admin Dashboard -->
    <template v-else>
      <div class="d-flex align-center mb-6">
        <div>
          <h1 class="text-h4">{{ $t('admin.title') }}</h1>
          <p class="text-subtitle-1 text-grey">{{ $t('admin.subtitle') }}</p>
        </div>
      </div>

      <!-- Stats Cards -->
      <v-row class="mb-6">
        <v-col cols="12" sm="6" md="3">
          <v-card color="primary" variant="tonal">
            <v-card-text class="text-center">
              <v-icon size="48" class="mb-2">mdi-account-group</v-icon>
              <div class="text-h4">{{ stats.totalUsers }}</div>
              <div class="text-subtitle-1">{{ $t('admin.totalUsers') }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card color="success" variant="tonal">
            <v-card-text class="text-center">
              <v-icon size="48" class="mb-2">mdi-account-check</v-icon>
              <div class="text-h4">{{ stats.activeUsers }}</div>
              <div class="text-subtitle-1">{{ $t('admin.activeUsers') }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card color="info" variant="tonal">
            <v-card-text class="text-center">
              <v-icon size="48" class="mb-2">mdi-post</v-icon>
              <div class="text-h4">{{ stats.totalPosts }}</div>
              <div class="text-subtitle-1">{{ $t('admin.totalPosts') }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card color="warning" variant="tonal">
            <v-card-text class="text-center">
              <v-icon size="48" class="mb-2">mdi-account-group</v-icon>
              <div class="text-h4">{{ stats.totalGroups }}</div>
              <div class="text-subtitle-1">{{ $t('admin.totalGroups') }}</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Navigation Tabs -->
      <v-card>
        <v-tabs v-model="activeTab" bg-color="primary">
          <v-tab value="users">
            <v-icon start>mdi-account-group</v-icon>
            {{ $t('admin.users') }}
          </v-tab>
          <v-tab value="posts">
            <v-icon start>mdi-post</v-icon>
            {{ $t('admin.posts') }}
          </v-tab>
          <v-tab value="groups">
            <v-icon start>mdi-account-multiple</v-icon>
            {{ $t('admin.groups') }}
          </v-tab>
          <v-tab value="pages">
            <v-icon start>mdi-file-document</v-icon>
            {{ $t('admin.pages') }}
          </v-tab>
          <v-tab value="faq">
            <v-icon start>mdi-frequently-asked-questions</v-icon>
            FAQ
          </v-tab>
          <v-tab value="kanban">
            <v-icon start>mdi-view-column</v-icon>
            Kanban
          </v-tab>
        </v-tabs>

        <v-window v-model="activeTab">
          <!-- Users Tab -->
          <v-window-item value="users">
            <admin-users-panel />
          </v-window-item>

          <!-- Posts Tab -->
          <v-window-item value="posts">
            <admin-posts-panel />
          </v-window-item>

          <!-- Groups Tab -->
          <v-window-item value="groups">
            <admin-groups-panel />
          </v-window-item>

          <!-- Pages Tab -->
          <v-window-item value="pages">
            <admin-pages-panel />
          </v-window-item>

          <!-- FAQ Tab -->
          <v-window-item value="faq">
            <admin-faq-panel />
          </v-window-item>

          <!-- Kanban Tab -->
          <v-window-item value="kanban">
            <div class="pa-4">
              <admin-kanban-panel :users="allUsers" />
            </div>
          </v-window-item>
        </v-window>
      </v-card>
    </template>
  </v-container>
</template>

<script setup>
/* eslint-disable */
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import api from '../services/api'
import AdminUsersPanel from '../components/admin/AdminUsersPanel.vue'
import AdminPostsPanel from '../components/admin/AdminPostsPanel.vue'
import AdminGroupsPanel from '../components/admin/AdminGroupsPanel.vue'
import AdminPagesPanel from '../components/admin/AdminPagesPanel.vue'
import AdminFaqPanel from '../components/admin/AdminFaqPanel.vue'
import AdminKanbanPanel from '../components/admin/AdminKanbanPanel.vue'

const store = useStore()

const loading = ref(true)
const isAdmin = ref(false)
const activeTab = ref('users')
const allUsers = ref([])
const stats = ref({
  totalUsers: 0,
  activeUsers: 0,
  totalPosts: 0,
  totalGroups: 0
})

const currentUser = computed(() => store.getters['auth/user'])

const checkAdmin = async () => {
  loading.value = true
  try {
    // Try to access admin endpoint - will fail if not admin
    const response = await api.get('/admin/users', { params: { limit: 1 } })
    isAdmin.value = true
    stats.value.totalUsers = response.data.total || 0
    
    // Get more stats
    const [postsRes, groupsRes] = await Promise.all([
      api.get('/admin/posts', { params: { limit: 1 } }),
      api.get('/admin/groups')
    ])
    
    stats.value.totalPosts = postsRes.data.total || 0
    stats.value.totalGroups = groupsRes.data.groups?.length || 0
    
    // Count active users & load all users for kanban assignee dropdown
    const [activeRes, allUsersRes] = await Promise.all([
      api.get('/admin/users', { params: { status: 'active', limit: 1 } }),
      api.get('/admin/users', { params: { limit: 999 } })
    ])
    stats.value.activeUsers = activeRes.data.total || 0
    allUsers.value = allUsersRes.data.users || []
    
  } catch (error) {
    console.error('Admin check failed:', error)
    isAdmin.value = false
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  checkAdmin()
})
</script>

<style scoped>
.admin-container {
  max-width: 1400px;
  margin: 0 auto;
  padding-top: 2rem;
}
</style>